import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { WebServicesService } from './web-services.service';
import {
  Country,
  GetDestinosMapaSitioAPI,
  GetEmpresasMapaSitioAPI,
  GetRutasMapaSitioAPI,
  GetTerminalesMapaSitioAPI,
  Style,
} from '../models';
import { THEME_STYLES, COUNTRIES } from '../constants';

@Injectable({
  providedIn: 'root',
})
export class GeneralService {
  paso: boolean = false;
  private styles: Style[] = [];
  private staticPages = [
    { path: '', lastModified: moment.now(), priority: 1 },
    { path: 'gestion-boletos', lastModified: moment.now(), priority: 0.8 },
    { path: 'ayuda', lastModified: moment.now(), priority: 0.8 },
    {
      path: 'terminos-y-condiciones',
      lastModified: moment.now(),
      priority: 0.8,
    },
    { path: 'politicas-privacidad', lastModified: moment.now(), priority: 0.8 },
    { path: 'politicas-cookies', lastModified: moment.now(), priority: 0.8 },
  ];

  public newComplement = new BehaviorSubject<string>('pasajes');

  constructor(
    @Inject(PLATFORM_ID)
    private platformId: any,
    private translateService: TranslateService,
    public ws: WebServicesService
  ) {}
  complementsArray: string[] = [
    'buses-altas-cumbres',
    'buses-casther',
    'buses-erbuc',
    'buses-expreso-del-sur',
    'buses-igillaima',
    'buses-jeldres',
    'buses-paravias',
    'buses-pullmantur',
    'buses-rcb',
    'buses-rimar',
    'buses-sanhueza',
    'buses-sol-del-pacifico',
    'buses-tra',
    'buses-transantin',
    'buses-viajaqui',
    'chi-ar',
    'chile-pruebas',
    'cikbus',
    'cikbus-express',
    'expreso-santa-cruz',
    'hualpen',
    'marga-taqsa',
    'movired',
    'narbus',
    'pasajes-buscador',
    'pullman-jc',
    'pullman-lago-penuelas',
    'pullman-setter',
    'ruta-bus-78',
    'transfer',
    'transfer-pruebas',
    'turismo-zaahj',
  ];

  get complementNew$() {
    return this.newComplement.asObservable();
  }

  set updateComplement(newValue: string) {
    this.newComplement.next(newValue);
  }

  checkComplement(value) {
    //en transfer se utiliza el mismo logo que pasajes
    return value === 'transfer' || value === 'transfer-pruebas'
      ? false
      : this.complementsArray.includes(value);
  }

  generateSitemapXml(): void {
    const baseUrl = 'https://chilepasajes.cl/en';
    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xml += `<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n`;
    // Add static pages
    this.staticPages.forEach((page) => {
      xml += ` <url>\n`;
      xml += ` <loc>${baseUrl}/${page.path}</loc>\n`;
      xml += ` <lastmod>${new Date(
        page.lastModified
      ).toISOString()}</lastmod>\n`;
      xml += ` <changefreq>monthly</changefreq>\n`;
      xml += ` <priority>${page.priority}</priority>\n`;
      xml += ` </url>\n`;
    });
    xml += `</urlset>\n`;
  }

  /**
   * TODO: USAR WINDOW.LOCATION.HOST
   * @returns
   */
  generateSitemapOperatorsXml() {
    let operators: any = [];
    const baseUrl = 'https://chilepasajes.cl/en/buses';
    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xml += `<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n`;

    this.ws.selectType('empresas', {}).then((res) => {
      const data: GetEmpresasMapaSitioAPI.ApiResponse = JSON.parse(
        res as string
      );

      // Add static pages
      if (data.success) {
        data.response.forEach((operator) => {
          xml += ` <url>\n`;
          xml += ` <loc>${baseUrl}/${operator.url}</loc>\n`;
          xml += ` <lastmod>${new Date().toISOString()}</lastmod>\n`;
          xml += ` <changefreq>monthly</changefreq>\n`;
          xml += ` <priority>${1.0}</priority>\n`;
          xml += ` </url>\n`;
        });
      }
      xml += `</urlset>\n`;
    });
  }

  generateSitemapCitiesXml() {
    const baseUrl = 'https://chilepasajes.cl/en/pasajes';
    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xml += `<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n`;

    this.ws.selectType('destinos', {}).then((res) => {
      const data: GetDestinosMapaSitioAPI.ApiResponse = JSON.parse(
        res as string
      );

      // Add static pages
      if (data.success) {
        data.response.forEach((destination) => {
          xml += ` <url>\n`;
          xml += ` <loc>${baseUrl}/${destination.url}</loc>\n`;
          xml += ` <lastmod>${new Date().toISOString()}</lastmod>\n`;
          xml += ` <changefreq>monthly</changefreq>\n`;
          xml += ` <priority>${1.0}</priority>\n`;
          xml += ` </url>\n`;
        });
      }
      xml += `</urlset>\n`;
    });
  }

  generateSitemapBusTerminalsXml() {
    const baseUrl = 'https://chilepasajes.cl/en/terminales';
    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xml += `<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n`;

    this.ws.selectType('terminales', {}).then((res) => {
      const data: GetTerminalesMapaSitioAPI.ApiResponse = JSON.parse(
        res as string
      );

      // Add static pages
      if (data.success) {
        data.response.forEach((terminal) => {
          xml += ` <url>\n`;
          xml += ` <loc>${baseUrl}/${terminal.url}</loc>\n`;
          xml += ` <lastmod>${new Date().toISOString()}</lastmod>\n`;
          xml += ` <changefreq>monthly</changefreq>\n`;
          xml += ` <priority>${1.0}</priority>\n`;
          xml += ` </url>\n`;
        });
        xml += `</urlset>\n`;
      }
    });
  }

  generateSitemapDestinationsXml() {
    const baseUrl = 'https://chilepasajes.cl/en/pasajes/ida';
    let xml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
    xml += `<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n`;

    this.ws.selectType('rutas', {}).then((res) => {
      const data: GetRutasMapaSitioAPI.ApiResponse = JSON.parse(
        res as string
      );

      // Add static pages
      if (data.success) {
        data.response.forEach((route) => {
          xml += ` <url>\n`;
          xml += ` <loc>${baseUrl}/${route.url}</loc>\n`;
          xml += ` <lastmod>${new Date().toISOString()}</lastmod>\n`;
          xml += ` <changefreq>monthly</changefreq>\n`;
          xml += ` <priority>${1.0}</priority>\n`;
          xml += ` </url>\n`;
        });
      }
      xml += `</urlset>\n`;
    });
  }

  formatDisplay(value, n, x, s, c, currency = '$') {
    value = typeof value == 'number' ? value : Number(value);
    var re = '\\d(?=(\\d{' + (x || 3) + '})+' + (n > 0 ? '\\D' : '$') + ')',
      num = value.toFixed(Math.max(0, ~~n));

    return `${currency} ${(c ? num.replace('.', c) : num).replace(
      new RegExp(re, 'g'),
      '$&' + (s || ',')
    )}`;
  }

  compareEmail(control: any) {
    if (control.parent) {
      if (control.parent.value.email.indexOf('.') == -1) {
        return {
          compareEmail: {
            valid: false,
          },
        };
      }
      if (
        control.parent.controls.email.value &&
        control.parent.controls.emailRepeat.value &&
        control.parent.controls.emailRepeat.value !==
          control.parent.controls.email.value &&
        control.parent.controls.emailRepeat.value &&
        control.parent.controls.email.value
      ) {
        return {
          compareEmail: {
            valid: false,
          },
        };
      }
    }
    return null;
  }

  getComplement(activatedRoute) {
    return new Promise((resolve, reject) => {
      activatedRoute.url.subscribe((url) => {
        return this.complementsArray.includes(url[1].path.toLowerCase())
          ? resolve(url[1].path.toLowerCase())
          : resolve('pasajes');
      });
    });
  }

  //TODO: extract to validators
  comparePassword(control: any) {
    if (control.parent) {
      if (
        control.parent.controls.password.value &&
        control.parent.controls.passwordRepeat.value &&
        control.parent.controls.passwordRepeat.value !==
          control.parent.controls.password.value
      ) {
        return {
          comparePasswordRegister: {
            valid: false,
          },
        };
      }
    }
    return null;
  }

  obtenerMarcaDeTiempo() {
    const today = new Date();
    let year = today.getFullYear().toString();
    let month = (today.getMonth() + 1).toString().padStart(2, '0');
    let day = today.getDate().toString().padStart(2, '0');
    let time = today.toLocaleTimeString().replace(/:/g, '');

    return year + month + day + time;
  }

  //TODO: delete
  formatDisplayNumero(value, n, x, s, c) {
    value = typeof value == 'number' ? value : Number(value);
    var re = '\\d(?=(\\d{' + (x || 3) + '})+' + (n > 0 ? '\\D' : '$') + ')',
      num = value.toFixed(Math.max(0, ~~n));

    return ` ${(c ? num.replace('.', c) : num).replace(
      new RegExp(re, 'g'),
      '$&' + (s || ',')
    )}`;
  }

  formatDisplayNumeroEntero(value, n, x, s, c) {
    value = typeof value == 'number' ? value : Number(value);
    var re = '\\d(?=(\\d{' + (x || 3) + '})+' + (n > 0 ? '\\D' : '$') + ')',
      num = value.toFixed(Math.max(0, ~~n));

    return `${(c ? num.replace('.', c) : num).replace(
      new RegExp(re, 'g'),
      '$&' + (s || ',')
    )}`;
  }

  //TODO: delete
  getMask(type: Number, value: string) {
    if (type == 1) {
      if (value) {
        if (value.length <= 8) {
          return '0.000.000-A';
        } else {
          return '00.000.000-A';
        }
      }
      return '00.000.000-A||0.000.000-A';
    }
    return '000000000000000'; //'000000000000000'
  }

  //TODO: delete
  /**
   * Agrega formato a un RUT chileno
   * 11111111 => 1.111.111-1
   * @param rutSinFormato
   * @returns
   */
  addFormatRut(rutSinFormato: string): string {
    if (rutSinFormato.length === 9) {
      return rutSinFormato.replace(
        /^(\d{2})(\d{3})(\d{3})(\d{1})$/,
        '$1.$2.$3-$4'
      );
    } else if (rutSinFormato.length === 8) {
      return rutSinFormato.replace(
        /^(\d{1})(\d{3})(\d{3})(\d{1})$/,
        '$1.$2.$3-$4'
      );
    } else {
      return rutSinFormato;
    }
  }

  getCountries(): Country[] {
    return COUNTRIES;
  }

  isBrowser() {
    return isPlatformBrowser(this.platformId);
  }

  getTranslation(keyword: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.translateService.get(keyword).subscribe((words: string) => {
        if (!words) {
          resolve('');
        }
        resolve(words);
      });
    });
  }

  eliminarPorDia() {
    if (typeof window !== 'undefined' && window.localStorage) {
      const today: string = new Date().toISOString().split('T')[0];
      const storedDay: string = window.localStorage.getItem('amount_last');

      if (!storedDay) {
        window.localStorage.setItem('amount_last', today);
      } else if (today > storedDay) {
        window.localStorage.clear();
        window.localStorage.setItem('amount_last', today);
      }
    }
  }

  public asignarEstilos(complemento: string) {
    try {
      const themeStyles = THEME_STYLES[complemento];

      if (!themeStyles) {
        throw new Error(
          `Estilos no encontrados para complemento ${complemento}`
        );
      }

      this.styles = themeStyles;
      this.applyStyles();
    } catch (error) {
      console.error('Error al asignar estilos', error);
    }
  }

  public applyStyles(): void {
    try {
      this.styles.forEach((style) => {
        document.documentElement.style.setProperty(
          `--${style.name}`,
          style.value
        );
      });
    } catch (error) {
      console.error('Error al aplicar estilos:', error);
    }
  }
}
